// Adyen wraps HTTP responses in Error.cause; Axios uses response.data.
export function getVoucherPaymentError(error) {
  let details = error || {};
  const result = {};
  for (let depth = 0; depth < 5 && details && typeof details === "object"; depth += 1) {
    for (const key of ["name", "message", "status", "httpStatus", "statusCode", "code", "errorCode", "errorType", "resultCode", "pspReference", "sessionResult", "refusalReason"]) {
      if (details[key] != null) result[key] = details[key];
    }
    if (details.response?.status != null) result.status = details.response.status;
    details = details.cause || details.options?.cause || details.response?.data || details.error || details.data;
  }
  return result;
}

export function isVoucherPaymentCancellation(error) {
  let value = error;
  for (let depth = 0; depth < 6 && value && typeof value === "object"; depth += 1) {
    const codes = [value.name, value.code, value.statusCode, value.errorCode, value.resultCode];
    if (codes.some((code) => ["CANCEL", "CANCELED", "CANCELLED", "USER_CANCELED", "USER_CANCELLED"].includes(String(code || "").toUpperCase()))) return true;
    // A generic AbortError may be a request timeout; only explicit sheet dismissal is cancellation.
    if (/user (closed|dismissed) (the )?(payment|wallet)|shopper cancel(?:led|ed)/i.test(value.message || "")) return true;
    value = value.cause || value.options?.cause || value.response?.data || value.error || value.data;
  }
  return false;
}

export function isVoucherPaymentServiceError(error) {
  if (isVoucherPaymentCancellation(error)) return false;
  const details = getVoucherPaymentError(error);
  return Boolean(
    Number(details.status || details.httpStatus) >= 400 ||
    details.name === "API_ERROR" ||
    /^\d{2}_\d{4}$/.test(String(details.errorCode || "")) ||
    (details.errorType && details.errorCode != null) ||
    ["REFUSED", "DECLINED", "ERROR", "INVALID_CARD", "NOT_SUPPORTED", "NOT_3D_SECURE"].includes(String(details.resultCode || "").toUpperCase())
  );
}

// Only an API error or explicit failed payment result opens the voucher modal.
// SDK configuration, validation, network failures and empty callbacks do not.
export function getVoucherPaymentFeedback(error, stage = "payment") {
  if (!isVoucherPaymentServiceError(error)) return null;
  const details = getVoucherPaymentError(error);
  const code = String(details.resultCode || details.errorCode || "").toUpperCase();
  if (details.errorCode === "14_0422") return {
    source: "api", title: "Your payment session has expired", sessionExpired: true,
    message: "Return to your billing details to refresh your payment options. Your voucher selection and details are saved.",
  };
  if (stage === "setup") return {
    source: "api", title: "We couldn’t load your payment options", allowRetry: true,
    message: "Please try loading them again. Your voucher selection and billing details are saved.",
    retryLabel: "Reload payment options",
  };
  if (["REFUSED", "DECLINED", "INVALID_CARD", "NOT_SUPPORTED", "NOT_3D_SECURE"].includes(code)) return {
    source: "api", title: code === "NOT_3D_SECURE" ? "Bank verification wasn’t completed" : "Payment wasn’t completed",
    message: getVoucherPaymentFailureMessage(code), allowRetry: true,
  };
  return {
    source: "api", title: "We couldn’t confirm your payment", allowRetry: false,
    message: "Check your bank or wallet activity before making another payment. If you see a charge, contact Natureland with your order reference so we can check it for you.",
  };
}

// Do not send SDK errors/Axios request configs to logs: they can contain
// authentication tokens, encrypted card fields, or customer details.
export function getPaymentLogDetails(error) {
  const details = getVoucherPaymentError(error);
  const safeCode = (value) => typeof value === "string" && /^[a-zA-Z0-9_-]{1,80}$/.test(value)
    ? value : undefined;
  return {
    name: safeCode(details.name),
    status: Number(details.status || details.httpStatus) || undefined,
    errorCode: safeCode(String(details.errorCode ?? "")),
    resultCode: safeCode(details.resultCode),
    pspReference: safeCode(details.pspReference),
  };
}

export function getVoucherPaymentFailureMessage(code) {
  switch (String(code || "").toUpperCase()) {
    case "REFUSED":
    case "DECLINED":
      return "Your payment was declined. Try again with different payment details, or choose another payment method. Your voucher selection is saved.";
    case "INVALID_CARD":
      return "Please check your card details and try again.";
    case "NOT_SUPPORTED":
      return "This payment method is not available for this purchase. Please choose another method.";
    case "NOT_3D_SECURE":
      return "Payment authentication was unsuccessful. Please try again or choose another payment method.";
    case "CANCEL":
    case "CANCELLED":
    case "CANCELED":
      return "Payment was cancelled. You can try again or choose another payment method.";
    default:
      return "We couldn’t confirm your payment. Check your bank or wallet activity before trying again. If you see a charge, contact Natureland with your order reference.";
  }
}
